"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateCareUnitInput = exports.createCareUnitInput = exports.careUnitOptionListSchema = exports.careUnitOptionSchema = exports.careUnitListSchema = exports.careUnitSchema = void 0;
exports.normaliseUnitText = normaliseUnitText;
exports.suggestCareUnit = suggestCareUnit;
exports.careUnitLocationText = careUnitLocationText;
/**
 * `RPM-031` — a building's care units: set up by an administrator, listed for the entry form, and
 * the rule that pre-selects one from a resident's record (`J-224`).
 *
 * A CARE UNIT IS CHARTVN'S, KEYED BY THE MIRROR'S FACILITY ID. The mirror stays the source of truth
 * for the building; floors, wings and units are rows ChartVN owns (`rpm_care_units`, `03-RPM-SCHEMA.md`
 * §4.2.10). They are added, edited and deactivated, never deleted.
 *
 * PRE-SELECTION IS AN EXACT MATCH OR NOTHING (`RPM-031` rule 2, `CD-1`). The resident's mirror unit is
 * trimmed, its inner whitespace collapsed and its case folded, and compared with each active unit's
 * label and listed mirror values the same way. One match pre-selects; none, two, or no unit in the
 * record leaves the choice to the nurse.
 */
const zod_1 = require("zod");
/** The mirror's identifier width. `carease.facilities.id` is varchar(20), and no FK reaches it. */
const MIRROR_ID = zod_1.z.string().min(1).max(20);
/** A label or a mirror value: shown and compared, never parsed. */
const UNIT_TEXT = zod_1.z.string().trim().min(1).max(120);
/** One care unit, as the administration list shows it. */
exports.careUnitSchema = zod_1.z.object({
    careUnitId: zod_1.z.uuid(),
    facilityId: MIRROR_ID,
    label: zod_1.z.string(),
    rooms: zod_1.z.string().nullable(),
    mirrorUnitValues: zod_1.z.array(zod_1.z.string()),
    isActive: zod_1.z.boolean(),
    updatedAt: zod_1.z.iso.datetime(),
});
/** A building's care units for its administrator, active first, then by label. */
exports.careUnitListSchema = zod_1.z.object({
    facilityId: MIRROR_ID,
    items: zod_1.z.array(exports.careUnitSchema),
});
/** What the entry form is offered: the active units only, and nothing an administrator edits. */
exports.careUnitOptionSchema = zod_1.z.object({
    careUnitId: zod_1.z.uuid(),
    label: zod_1.z.string(),
    rooms: zod_1.z.string().nullable(),
});
exports.careUnitOptionListSchema = zod_1.z.object({
    facilityId: MIRROR_ID,
    items: zod_1.z.array(exports.careUnitOptionSchema),
});
/**
 * Adding a care unit. `mirrorUnitValues` are the strings a resident record may carry for this unit
 * ("2W" for "2nd floor"); each is compared, never parsed.
 */
exports.createCareUnitInput = zod_1.z.strictObject({
    label: UNIT_TEXT,
    rooms: zod_1.z.string().trim().max(500).optional(),
    mirrorUnitValues: zod_1.z.array(UNIT_TEXT).max(50).optional(),
});
/**
 * Editing or deactivating one. Deactivation is `isActive: false`; there is no reactivation here and
 * no delete anywhere. At least one field is sent.
 */
exports.updateCareUnitInput = zod_1.z
    .strictObject({
    label: UNIT_TEXT.optional(),
    rooms: zod_1.z.string().trim().max(500).nullable().optional(),
    mirrorUnitValues: zod_1.z.array(UNIT_TEXT).max(50).optional(),
    isActive: zod_1.z.literal(false).optional(),
})
    .refine((v) => Object.keys(v).length > 0, {
    message: "Send at least one field to change.",
});
/** `RPM-031` rule 2's comparison form: trimmed, inner whitespace collapsed, case folded. */
function normaliseUnitText(value) {
    return value.trim().replace(/\s+/g, " ").toLowerCase();
}
/**
 * The one care unit a resident's mirror unit pre-selects, or `null`. Exactly one active unit whose
 * label or a listed mirror value matches after normalising; zero or several leave it to the nurse.
 */
function suggestCareUnit(mirrorUnit, units) {
    if (mirrorUnit === null || mirrorUnit === undefined)
        return null;
    const wanted = normaliseUnitText(mirrorUnit);
    if (wanted === "")
        return null;
    const matches = units.filter((u) => u.isActive &&
        [u.label, ...u.mirrorUnitValues].some((text) => normaliseUnitText(text) === wanted));
    return matches.length === 1 ? (matches[0]?.careUnitId ?? null) : null;
}
/**
 * An alert's location (`RPM-022`, `CR-43`): the care unit's label, then ", room " and ", bed " where
 * the resident's mirror row carries them. The room and the bed are the ones stored when the workup
 * opened, so a resident moved in the mirror later leaves the alert saying where she was.
 */
function careUnitLocationText(label, room, bed) {
    const part = (name, value) => value !== null && value !== undefined && value.trim() !== "" ? `, ${name} ${value.trim()}` : "";
    return `${label}${part("room", room)}${part("bed", bed)}`;
}
//# sourceMappingURL=rpm-care-units.js.map